import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { PencilIcon } from '@heroicons/react/24/outline'
import type { TypeAheadOption } from '../../components/TypeAheadSelect'

// WEB-6573 follow-up: inline property combobox — click clears the current value
// so typing is obvious; faded placeholder keeps context. Typing filters the dropdown.

export interface PropertyPickerProps {
  /** Fallback display / placeholder when nothing is selected. */
  label: string
  options: TypeAheadOption[]
  value: string | null
  onSelect: (cuid: string) => void
  /** Input styling — defaults to the colored header treatment. */
  className?: string
  /** Stacked filter-field mode — full-width control matching SaberControlBar filters. */
  field?: boolean
  /** Read-only context beside the input (e.g. utility), shown before the pencil icon. */
  suffix?: React.ReactNode
  id?: string
}

const DEFAULT_INPUT =
  'max-w-full min-w-[1ch] border-0 bg-transparent p-0 text-lg font-semibold leading-7 text-indigo-600 ' +
  'outline-none ring-0 focus:ring-0 shadow-none ' +
  'placeholder:text-indigo-400/70 dark:text-indigo-400 dark:placeholder:text-indigo-500/70'

const INPUT_SIZER = 'pointer-events-none invisible absolute left-0 top-0 whitespace-pre text-lg font-semibold'

export const PROPERTY_FIELD_TRIGGER =
  'inline-flex w-full min-w-[8.5rem] items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 ' +
  'text-left focus-within:ring-2 focus-within:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700/60'

const FIELD_INPUT =
  'w-full min-w-0 bg-transparent text-xs font-medium text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500'

const PENCIL = 'h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500'

export const PropertyPicker: React.FC<PropertyPickerProps> = ({
  label,
  options,
  value,
  onSelect,
  className,
  field = false,
  suffix,
  id = 'saber-property-picker'
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [hi, setHi] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const sizerRef = useRef<HTMLSpanElement>(null)
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined)

  const selected = options.find((o) => o.value === value)
  const displayLabel = selected?.label ?? label

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent): void => {
      if (ref.current != null && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const q = query.toLowerCase()
  const filtered = options.filter(
    (o) =>
      o.label.toLowerCase().includes(q) ||
      (o.sublabel ?? '').toLowerCase().includes(q) ||
      (o.detail ?? '').toLowerCase().includes(q)
  )
  const highlight = filtered.length === 0 ? -1 : Math.min(hi, filtered.length - 1)

  const pick = (o: TypeAheadOption): void => {
    onSelect(o.value)
    setOpen(false)
    setQuery('')
    inputRef.current?.blur()
  }

  const highlightForQuery = (nextQuery: string): number => {
    if (nextQuery !== '') return 0
    const currentIdx = options.findIndex((o) => o.value === value)
    return currentIdx >= 0 ? currentIdx : 0
  }

  const onFocus = (): void => {
    setOpen(true)
    setQuery('')
    setHi(highlightForQuery(''))
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value
    setQuery(next)
    setOpen(true)
    setHi(highlightForQuery(next))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setHi((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHi((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlight >= 0 && filtered[highlight] != null) pick(filtered[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setQuery('')
      inputRef.current?.blur()
    }
  }

  const wrapperClass = className ?? (field ? PROPERTY_FIELD_TRIGGER : 'inline-flex items-center gap-1.5')
  const inputClass = field ? FIELD_INPUT : DEFAULT_INPUT
  const inputValue = open ? query : displayLabel
  const sizerText = open ? (query !== '' ? query : displayLabel) : displayLabel

  useLayoutEffect(() => {
    if (field) return
    setInputWidth(sizerRef.current?.scrollWidth)
  }, [sizerText, field])

  useLayoutEffect(() => {
    if (!open || highlight < 0) return
    listboxRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${highlight}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [open, highlight])

  return (
    <div ref={ref} className={field ? 'relative block w-full' : 'relative inline-flex max-w-full'}>
      <div
        className={wrapperClass}
        onClick={() => {
          inputRef.current?.focus()
        }}
      >
        {field ? (
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={`${id}-listbox`}
            aria-label="Property"
            autoComplete="off"
            value={inputValue}
            placeholder={displayLabel}
            onFocus={onFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            className={inputClass}
          />
        ) : (
          <span className="relative inline-block max-w-full align-middle">
            <span ref={sizerRef} className={INPUT_SIZER} aria-hidden>
              {sizerText}
            </span>
            <input
              ref={inputRef}
              id={id}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls={`${id}-listbox`}
              aria-label="Property"
              autoComplete="off"
              value={inputValue}
              placeholder={displayLabel}
              style={inputWidth != null ? { width: `${inputWidth}px` } : undefined}
              onFocus={onFocus}
              onChange={onChange}
              onKeyDown={onKeyDown}
              className={inputClass}
            />
          </span>
        )}
        {!field && suffix}
        <PencilIcon className={PENCIL} aria-hidden />
      </div>
      {open && (
        <div
          ref={listboxRef}
          id={`${id}-listbox`}
          role="listbox"
          className={classnames(
            'absolute left-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-800',
            field ? 'w-full min-w-[14rem]' : 'w-72'
          )}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">
              {query === '' ? 'No properties' : `No matches for “${query}”`}
            </div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                role="option"
                data-option-index={i}
                aria-selected={o.value === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseEnter={() => setHi(i)}
                className={classnames(
                  'flex w-full items-start justify-between gap-3 px-3 py-1.5 text-left text-sm',
                  i === highlight ? 'bg-indigo-50 dark:bg-indigo-500/10' : undefined,
                  o.value === value
                    ? 'font-medium text-indigo-700 dark:text-indigo-300'
                    : 'text-slate-700 dark:text-slate-200'
                )}
              >
                <div className="min-w-0">
                  <div>{o.label}</div>
                  {o.sublabel != null && (
                    <div className="text-xs font-normal text-slate-500 dark:text-slate-400">{o.sublabel}</div>
                  )}
                </div>
                {o.detail != null && (
                  <span className="shrink-0 whitespace-nowrap text-xs font-normal text-slate-400 dark:text-slate-500">
                    {o.detail}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default PropertyPicker
